// =============================================================================
// Origin UI — src/pages/offers/OffersPage.tsx
// Offer pipeline: list view + Kanban view with toggle.
// Batch 1.5 (list) + 1.8 (Kanban).
// =============================================================================

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/common';
import { offerApi, OfferListItem } from '@/services/offerApi';
import styles from './OffersPage.module.css';

const STATUS_OPTIONS = ['All', 'Draft', 'Sent', 'Accepted', 'Onboarding', 'Declined', 'Expired', 'Withdrawn'];

const statusColors: Record<string, string> = {
  Draft: '#6B7280',
  Sent: '#2563EB',
  Accepted: '#059669',
  Onboarding: '#00B0B9',
  Declined: '#DC2626',
  Expired: '#9CA3AF',
  Withdrawn: '#F59E0B',
};

// Kanban columns — active pipeline stages only
const KANBAN_COLUMNS = ['Draft', 'Sent', 'Accepted', 'Onboarding'] as const;
// Terminal/inactive statuses hidden by default
const TERMINAL_STATUSES = ['Declined', 'Expired', 'Withdrawn'];

type ViewMode = 'list' | 'kanban';

export function OffersPage() {
  const navigate = useNavigate();
  const [offers, setOffers] = useState<OfferListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [showTerminal, setShowTerminal] = useState(false);

  useEffect(() => {
    loadOffers();
  }, [statusFilter]);

  async function loadOffers() {
    try {
      setLoading(true);
      setError('');
      const params: { status?: string; search?: string } = {};
      if (statusFilter !== 'All') params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      const data = await offerApi.getOffers(params);
      setOffers(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load offers');
    } finally {
      setLoading(false);
    }
  }

  function handleSearch() {
    loadOffers();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') loadOffers();
  }

  function formatDate(dateStr: string | null): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  // Group offers by status for Kanban
  const offersByStatus = useMemo(() => {
    const grouped: Record<string, OfferListItem[]> = {};
    for (const col of KANBAN_COLUMNS) {
      grouped[col] = [];
    }
    for (const status of TERMINAL_STATUSES) {
      grouped[status] = [];
    }
    for (const offer of offers) {
      if (!grouped[offer.status]) grouped[offer.status] = [];
      grouped[offer.status].push(offer);
    }
    return grouped;
  }, [offers]);

  const terminalCount = useMemo(() => {
    return TERMINAL_STATUSES.reduce((sum, s) => sum + (offersByStatus[s]?.length || 0), 0);
  }, [offersByStatus]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Offers</h1>
          <p className={styles.subtitle}>{offers.length} offer{offers.length !== 1 ? 's' : ''}</p>
        </div>
        <div className={styles.headerActions}>
          {/* View toggle */}
          <div className={styles.viewToggle}>
            <button
              className={`${styles.toggleBtn} ${viewMode === 'list' ? styles.toggleActive : ''}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
            <button
              className={`${styles.toggleBtn} ${viewMode === 'kanban' ? styles.toggleActive : ''}`}
              onClick={() => setViewMode('kanban')}
            >
              Pipeline
            </button>
          </div>
          <button className={styles.primaryButton} onClick={() => navigate('/offers/new')}>
            + New Offer
          </button>
        </div>
      </div>

      {/* Filters — list view only */}
      {viewMode === 'list' && (
        <Card className={styles.filtersCard}>
          <div className={styles.filters}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Status</label>
              <select
                className={styles.filterSelect}
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Search</label>
              <div className={styles.searchRow}>
                <input
                  className={styles.searchInput}
                  type="text"
                  placeholder="Name, email, or job title..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button className={styles.searchButton} onClick={handleSearch}>Search</button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Error */}
      {error && <div className={styles.error}>{error}</div>}

      {/* Loading */}
      {loading ? (
        <div className={styles.loading}>Loading offers...</div>
      ) : offers.length === 0 ? (
        <div className={styles.empty}>No offers found.</div>
      ) : viewMode === 'list' ? (
        /* ─── List View ─── */
        <Card className={styles.tableCard}>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Job Title</th>
                  <th>Office</th>
                  <th>Department</th>
                  <th>Type</th>
                  <th>Start Date</th>
                  <th>Expiry</th>
                  <th>Status</th>
                  <th>Visa</th>
                </tr>
              </thead>
              <tbody>
                {offers.map(offer => (
                  <tr
                    key={offer.offerId}
                    className={styles.tableRow}
                    onClick={() => navigate(`/offers/${offer.offerId}`)}
                  >
                    <td>
                      <div className={styles.candidateName}>{offer.candidateName}</div>
                      <div className={styles.candidateEmail}>{offer.candidateEmail}</div>
                    </td>
                    <td>{offer.jobTitle}</td>
                    <td>{offer.officeName}</td>
                    <td>{offer.departmentName}</td>
                    <td>{offer.employmentType}</td>
                    <td>{formatDate(offer.proposedStartDate)}</td>
                    <td>{formatDate(offer.offerExpiryDate)}</td>
                    <td>
                      <span
                        className={styles.statusPill}
                        style={{ backgroundColor: statusColors[offer.status] || '#6B7280' }}
                      >
                        {offer.status}
                      </span>
                    </td>
                    <td>
                      {offer.visaRequired ? (
                        <span className={styles.visaBadge}>{offer.visaStatus || 'Required'}</span>
                      ) : (
                        <span className={styles.noVisa}>—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        /* ─── Kanban View ─── */
        <div className={styles.kanban}>
          <div className={styles.kanbanColumns}>
            {KANBAN_COLUMNS.map(status => (
              <div key={status} className={styles.kanbanColumn}>
                <div className={styles.kanbanHeader}>
                  <span
                    className={styles.kanbanHeaderDot}
                    style={{ backgroundColor: statusColors[status] }}
                  />
                  <span className={styles.kanbanHeaderTitle}>{status}</span>
                  <span className={styles.kanbanHeaderCount}>{offersByStatus[status]?.length || 0}</span>
                </div>
                <div className={styles.kanbanCards}>
                  {(offersByStatus[status] || []).map(offer => (
                    <div
                      key={offer.offerId}
                      className={styles.kanbanCard}
                      onClick={() => navigate(`/offers/${offer.offerId}`)}
                    >
                      <div className={styles.kanbanCardName}>{offer.candidateName}</div>
                      <div className={styles.kanbanCardJob}>{offer.jobTitle}</div>
                      <div className={styles.kanbanCardMeta}>
                        <span>{offer.officeName}</span>
                        {offer.proposedStartDate && (
                          <span>Start: {formatDate(offer.proposedStartDate)}</span>
                        )}
                      </div>
                      {offer.visaRequired && (
                        <span className={styles.kanbanVisaBadge}>{offer.visaStatus || 'Visa Required'}</span>
                      )}
                    </div>
                  ))}
                  {(offersByStatus[status] || []).length === 0 && (
                    <div className={styles.kanbanEmpty}>No offers</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Terminal statuses toggle */}
          {terminalCount > 0 && (
            <div className={styles.terminalToggle}>
              <button
                className={styles.terminalToggleBtn}
                onClick={() => setShowTerminal(!showTerminal)}
              >
                {showTerminal ? 'Hide' : 'Show'} declined / expired / withdrawn ({terminalCount})
              </button>
            </div>
          )}

          {showTerminal && (
            <div className={styles.kanbanColumns}>
              {TERMINAL_STATUSES.map(status => {
                const items = offersByStatus[status] || [];
                if (items.length === 0) return null;
                return (
                  <div key={status} className={`${styles.kanbanColumn} ${styles.kanbanColumnTerminal}`}>
                    <div className={styles.kanbanHeader}>
                      <span
                        className={styles.kanbanHeaderDot}
                        style={{ backgroundColor: statusColors[status] }}
                      />
                      <span className={styles.kanbanHeaderTitle}>{status}</span>
                      <span className={styles.kanbanHeaderCount}>{items.length}</span>
                    </div>
                    <div className={styles.kanbanCards}>
                      {items.map(offer => (
                        <div
                          key={offer.offerId}
                          className={`${styles.kanbanCard} ${styles.kanbanCardTerminal}`}
                          onClick={() => navigate(`/offers/${offer.offerId}`)}
                        >
                          <div className={styles.kanbanCardName}>{offer.candidateName}</div>
                          <div className={styles.kanbanCardJob}>{offer.jobTitle}</div>
                          <div className={styles.kanbanCardMeta}>
                            <span>{offer.officeName}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
